import { copperButton, metalPanel, sectionHeading, woodPanel } from '@/shared/ui/game-theme';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useT, t } from '@/i18n';

export interface WanderingMerchantOfferRow {
  slotIndex: number;
  cost: number;
  sold: boolean;
}

interface WanderingMerchantDialogProps {
  npcName: string;
  message: string;
  phase: 'approach' | 'shop';
  offers: WanderingMerchantOfferRow[];
  heroGold: number;
  purchaseErrorVersion: number;
  onBuySlot: (slotIndex: number) => void;
  onLeave: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
};

const backdropStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  width: '92%',
  maxWidth: 360,
  maxHeight: '78%',
  overflowY: 'auto',

  padding: '18px 16px 14px',
  animation: 'wander-merchant-pop 0.3s ease-out',
  ...woodPanel,
};

const titleStyle: CSSProperties = {
  fontSize: 16,

  marginBottom: 6,
  textAlign: 'center',
  ...sectionHeading,
};

const messageStyle: CSSProperties = {
  fontSize: 12,
  color: '#bfb2a0',
  lineHeight: 1.45,
  marginBottom: 12,
  textAlign: 'center',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '10px 8px',
  marginBottom: 8,

  border: '1px solid rgba(218, 165, 32, 0.15)',
  ...metalPanel,
};

export function WanderingMerchantDialog({
  npcName,
  message,
  phase,
  offers,
  heroGold,
  purchaseErrorVersion,
  onBuySlot,
  onLeave,
}: WanderingMerchantDialogProps) {
  const tr = useT();
  const [pendingSlot, setPendingSlot] = useState<number | null>(null);
  const previousSoldSlots = useRef(new Set(offers.filter((offer) => offer.sold).map((offer) => offer.slotIndex)));

  const previousPurchaseErrorVersion = useRef(purchaseErrorVersion);

  useEffect(() => {
    if (purchaseErrorVersion !== previousPurchaseErrorVersion.current) setPendingSlot(null);
    previousPurchaseErrorVersion.current = purchaseErrorVersion;
  }, [purchaseErrorVersion]);

  useEffect(() => {
    const soldSlots = new Set(offers.filter((offer) => offer.sold).map((offer) => offer.slotIndex));
    const purchaseConfirmed = pendingSlot !== null && offers.some((offer) =>
      offer.slotIndex === pendingSlot && offer.sold && !previousSoldSlots.current.has(offer.slotIndex),
    );
    previousSoldSlots.current = soldSlots;
    if (pendingSlot !== null && purchaseConfirmed) {
      setPendingSlot(null);
    }
  }, [offers, pendingSlot]);

  if (phase === 'approach') {
    return null;
  }

  const handleBuy = (slotIndex: number, cost: number, sold: boolean) => {
    if (sold || pendingSlot != null || heroGold < cost) return;
    setPendingSlot(slotIndex);
    onBuySlot(slotIndex);
  };

  return (
    <>
      <style>{`
        @keyframes wander-merchant-pop {
          0%   { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={overlayStyle}>
        <div style={backdropStyle} onClick={onLeave} />
        <div style={cardStyle}>
          <div style={titleStyle}>{npcName}</div>
          <div style={messageStyle}>{message}</div>
          {offers.map((offer) => {
            const canBuy = !offer.sold && heroGold >= offer.cost && pendingSlot === null;
            return (
              <div key={offer.slotIndex} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: offer.sold ? '#9f9687' : '#d7b66f' }}>
                    {offer.sold ? tr.mysteryItemSold : tr.mysteryItemLabel}
                  </div>
                  <div style={{ fontSize: 12, color: '#d7b66f', marginTop: 2 }}>
                    {offer.cost} {tr.gold}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={offer.sold || !canBuy}
                  onClick={() => handleBuy(offer.slotIndex, offer.cost, offer.sold)}
                  style={{
                ...copperButton,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: offer.sold || !canBuy ? 'default' : 'pointer',
                    backgroundColor: canBuy ? 'rgba(68, 200, 68, 0.25)' : 'rgba(80, 80, 80, 0.2)',
                    color: canBuy ? '#9bb58a' : '#9f9687',
                  }}
                >
                  {offer.sold ? tr.soldOut : tr.buy}
                </button>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: '#9f9687', textAlign: 'center', marginTop: 4, marginBottom: 10 }}>
            {t(tr.yourGoldLabel, { amount: heroGold })}
          </div>
          <button
            type="button"
            onClick={onLeave}
            style={{
                ...copperButton,
              width: '100%',
              padding: '10px 0',
              borderRadius: 10,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              backgroundColor: 'rgba(200, 68, 68, 0.15)',
              color: '#cf9686',
            }}
          >
            {tr.leaveShop}
          </button>
        </div>
      </div>
    </>
  );
}
